/*
 * @Author: yanxia@tencent.com
 * @Date: 2022-04-07 21:11:27
 * @LastEditors: yanxia@tencent.com
 * @LastEditTime: 2022-04-29 13:10:33
 * @Description: 移动端注册
 */

import { getLoginCgiHost } from '@tencent/syzs-login-sdk-utils';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import DatePicker from 'rmc-date-picker';
import 'rmc-date-picker/assets/index.css';
import 'rmc-picker/assets/index.css';
import { BaseModel, Select } from '../../component';
import i18n from '../../i18n';
import { addMobileClassName, fill2Zero, LoginRequest, postMessageToParent } from '../../lib';
import { AgreePolicy } from './GoogleAndFaceBookButtons';
import { IOptions, requestCountry, saveUser } from './RegisterMain';
import './RegisterMainMobile.less';

export enum RegisterStep {
  /** 填写生日、地区 */
  InformationRequired = 'InformationRequired',
  /** 服务条款、隐私协议 */
  OutboundPolicyNoticeAndPrivacyPolicy = 'OutboundPolicyNoticeAndPrivacyPolicy',
  /** 取消注册 */
  OnboardingIncomplete = 'OnboardingIncomplete',
}

const registerTitleMap = {
  [RegisterStep.InformationRequired]: i18n.l('InformationRequired'),
  [RegisterStep.OutboundPolicyNoticeAndPrivacyPolicy]: 'GameLoop',
  [RegisterStep.OnboardingIncomplete]: i18n.l('OnboardingIncomplete'),
};

const getRegisterDescription = (registerStep: RegisterStep, needDataTransferNotice?: boolean) => {
  switch (registerStep) {
    case RegisterStep.InformationRequired:
      return '';
    case RegisterStep.OutboundPolicyNoticeAndPrivacyPolicy:
      return needDataTransferNotice ? i18n.l('DataTransferNoticeDescription') : 'Please read and understand the GameLoop Terms of Service  and Privacy Policy.';
    case RegisterStep.OnboardingIncomplete:
      return i18n.l('OnboardingIncompleteDescription');
  }
};

export const formatBirthDay = (date: Date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString();
  const day = date.getDate().toString();
  return `${year}-${fill2Zero(month)}-${fill2Zero(day)}`;
};

export interface IRegisterInfo {
  underAgeRequirement: boolean;
  birthDay: Date;
  /** 是否在PrivacyPolicy点击了下一步 */
  onPrivacyPolicyNext: boolean;
  lastRegisterStep: RegisterStep;
  countryList: IOptions[];
  countryCode?: number;
}

export const getOutboundPolicyNotice = async (countryCode: number) => {
  const result = await LoginRequest.request(`${getLoginCgiHost()}/v3/get_outbound_policy_notice`, { country_code: countryCode });

  const { status_code } = result;

  return status_code === 0;
};

export const RegisterMainMobile: React.FC = () => {
  const { control, watch, setValue, register } = useForm();
  const { agreeTerms, agreePrivacy, countryCode, agreeDataTransferNotice } = watch();

  /** 注册步骤 */
  const [registerStep, setRegisterStep] = useState<RegisterStep>(RegisterStep.InformationRequired);

  const [registerInfo, setRegisterInfoPrivate] = useState<IRegisterInfo>({
    underAgeRequirement: false,
    birthDay: new Date(),
    onPrivacyPolicyNext: false,
    lastRegisterStep: RegisterStep.InformationRequired,
    countryList: [],
  });

  const [needDataTransferNotice, setNeedDataTransferNotice] = useState(false);

  const stepCountryCode = countryCode || registerInfo.countryCode;

  useEffect(() => {
    const init = async () => {
      const status = await getOutboundPolicyNotice(+stepCountryCode);

      setNeedDataTransferNotice(status);
    };

    void init();
  }, [stepCountryCode]);

  const setRegisterInfo = (state: Partial<IRegisterInfo>) => setRegisterInfoPrivate(current => (
    { ...current, ...state }
  ));

  // 生日、地区
  const onDateChange = (date: Date) => {
    setRegisterInfo({ birthDay: date, underAgeRequirement: false });
  };

  // 初始化国家
  useEffect(() => {
    const init = async () => {
      const { countriesItem, defaultKey } = await requestCountry();

      setRegisterInfo({ countryList: countriesItem });

      if (defaultKey) {
        setValue('countryCode', defaultKey);
      }
    };

    void init();
  }, [setValue]);

  const informationRequiredContent = (
    <div className="information-require-content">
      <div className="title">{i18n.l('countryAndRegion')}</div>
      <div className="description">{i18n.l('countryAndRegionDescription')}</div>
      <Select
        name="countryCode"
        control={control}
        options={registerInfo.countryList}
        defaultValue="Country / Region"
        useFastSelect
      />
      <div className="title">{ i18n.l('DateOfBirth') }</div>
      <DatePicker
        defaultDate={new Date()}
        mode="date"
        locale={{
          year: '年',
          month: '月',
          day: '日',
          hour: '时',
          minute: '分',
          am: '上午',
          pm: '下午',
        }}
        maxDate={new Date()}
        minDate={new Date(1900, 1, 1, 0, 0, 0)}
        onDateChange={onDateChange}
      />
      { registerInfo.underAgeRequirement && <div className="under-age-requirement">{ i18n.l('UnderAgeRequirement') }</div> }
    </div>
  );

  const privacyPolicyContent = (
    <div className="privacy-policy-content">
      <AgreePolicy
        message={i18n.l('readAndAgreeBefore')}
        messageAfter={i18n.l('readAndAgreeAfter')}
        name={i18n.l('TermsOfService')}
        url="https://webapp.gameloop.com/gameloop-mobile/TermsOfService.html"
        register={register}
        field="agreeTerms"
        error={registerInfo.onPrivacyPolicyNext && !agreeTerms}
      />
      <AgreePolicy
        message={i18n.l('readAndAcknowledgeBefore')}
        messageAfter={i18n.l('readAndAcknowledgeAfter')}
        name={i18n.l('PrivacyPolicy')}
        register={register}
        field="agreePrivacy"
        url="https://webapp.gameloop.com/gameloop-mobile/PrivacyPolicy.html"
        error={registerInfo.onPrivacyPolicyNext && !agreePrivacy}
      />
      {
        needDataTransferNotice && (
          <AgreePolicy
            message={i18n.l('agreeToDataTransfer')}
            messageAfter={i18n.l('agreeToDataTransferAfter')}
            name={i18n.l('DataTransferNotice')}
            register={register}
            field="agreeDataTransferNotice"
            url={`https://nutty.qq.com/nutty/ssr/8797.html?countryCode=${countryCode}&showPolicy=0`}
            error={registerInfo.onPrivacyPolicyNext && !agreeDataTransferNotice}
          />
        )
      }
    </div>
  );

  const handleInformationRequired = async () => {
    const result = await LoginRequest.request(`${getLoginCgiHost()}/v3/verify_visitor`, {
      date_of_birth: formatBirthDay(registerInfo.birthDay),
      register_country_code: +countryCode,
    });

    const { status_code } = result;

    if (status_code !== 0) {
      setRegisterInfo({ underAgeRequirement: true });
      return;
    }

    setRegisterInfo({ countryCode: +countryCode });
    setRegisterStep(RegisterStep.OutboundPolicyNoticeAndPrivacyPolicy);
  };


  const handleOutboundPolicyNoticeAndPrivacyPolicy = async () => {
    setRegisterInfo({ onPrivacyPolicyNext: true });

    if (!(agreeTerms && agreePrivacy && agreeDataTransferNotice)) {
      return;
    }

    if (!registerInfo.countryCode) return;

    await saveUser({ birthDay: formatBirthDay(registerInfo.birthDay), countryCode: registerInfo.countryCode });
  };

  const handleOnboardingIncomplete = async () => {
    void postMessageToParent('CLOSE_LOGIN_MODAL', {});
  };

  const onNext = () => {
    switch (registerStep) {
      case RegisterStep.InformationRequired:
        void handleInformationRequired();
        break;
      case RegisterStep.OutboundPolicyNoticeAndPrivacyPolicy:
        void handleOutboundPolicyNoticeAndPrivacyPolicy();
        break;
      case RegisterStep.OnboardingIncomplete:
        void handleOnboardingIncomplete();
        break;
    }
  };

  const onCancel = () => {
    if (registerStep === RegisterStep.OnboardingIncomplete) {
      setRegisterStep(registerInfo.lastRegisterStep);
      return;
    }

    setRegisterInfo({ lastRegisterStep: registerStep });
    setRegisterStep(RegisterStep.OnboardingIncomplete);
  };

  return (
    <div className={addMobileClassName('login-v2-main')}>
      <BaseModel
        showLeftModule={false}
        showClose={false}
        title={registerTitleMap[registerStep]}
        description={getRegisterDescription(registerStep, needDataTransferNotice)}
        okText={registerStep === RegisterStep.OutboundPolicyNoticeAndPrivacyPolicy ? i18n.l('Confirm') : i18n.l('Next')}
        cancelText={i18n.l('Cancel')}
        showButtons
        onOk={onNext}
        onCancel={onCancel}
      >
        <div className="login-v2-register-main-mobile">
          { registerStep === RegisterStep.InformationRequired && informationRequiredContent }
          { registerStep === RegisterStep.OutboundPolicyNoticeAndPrivacyPolicy && privacyPolicyContent }
        </div>
      </BaseModel>
    </div>
  );
};
